import { useId, useMemo } from 'react';
import { PickList, type PickItem } from '@/components/PickList';

/**
 * Who an announcement (or a shared reminder, which has the same shape) is
 * for. Empty lists on the server mean "everyone", so the `all*` flags are what
 * the person chose and the lists are only sent when they chose "only some".
 */
export interface Audience {
  allRoles: boolean;
  roleCodes: string[];
  allFarms: boolean;
  farmIds: string[];
}

export interface AudienceOptions {
  roles: { code: string; nameEn: string; nameUr?: string | null }[];
  farms: { id: string; code: string; nameEn: string }[];
}

/** The most the server takes in one announcement (its ArrayMaxSize). */
export const MAX_ROLES = 50;
export const MAX_FARMS = 500;

export const EVERYONE: Audience = { allRoles: true, roleCodes: [], allFarms: true, farmIds: [] };

/** The audience the server would actually store, for sending. */
export function audiencePayload(a: Audience): { roleCodes: string[]; farmIds: string[] } {
  return { roleCodes: a.allRoles ? [] : a.roleCodes, farmIds: a.allFarms ? [] : a.farmIds };
}

/**
 * What is wrong with the choice, in plain words. "Only some roles" with none
 * ticked would reach the server as an empty list, which means everyone: the
 * opposite of what the person meant.
 */
export function audienceProblem(a: Audience): string | null {
  if (!a.allRoles && a.roleCodes.length === 0) return 'Tick at least one role, or choose Everyone.';
  if (!a.allFarms && a.farmIds.length === 0) return 'Tick at least one farm, or choose Every farm.';
  if (!a.allRoles && a.roleCodes.length > MAX_ROLES) return `One announcement can name at most ${MAX_ROLES} roles.`;
  if (!a.allFarms && a.farmIds.length > MAX_FARMS) return `One announcement can name at most ${MAX_FARMS} farms.`;
  return null;
}

function listNames(names: string[], max = 2): string {
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')} +${names.length - max} more`;
}

/** One line for the summary row: "Everyone · every farm". */
export function describeAudience(a: Audience, options: AudienceOptions | undefined): string {
  const roleName = (code: string) => options?.roles.find((r) => r.code === code)?.nameEn ?? code;
  const farm = (id: string) => options?.farms.find((f) => f.id === id);

  const people = a.allRoles
    ? 'Everyone'
    : a.roleCodes.length === 0
      ? 'No roles chosen'
      : listNames(a.roleCodes.map(roleName));
  const farms = a.allFarms
    ? 'every farm'
    : a.farmIds.length === 0
      ? 'no farms chosen'
      : a.farmIds.length === 1
        ? (farm(a.farmIds[0]!)?.nameEn ?? '1 farm')
        : `${a.farmIds.length} farms`;
  return `${people} · ${farms}`;
}


export function AudiencePicker({
  value,
  onChange,
  options,
  canPickEveryFarm,
}: {
  value: Audience;
  onChange: (next: Audience) => void;
  options: AudienceOptions | undefined;
  /** False for someone scoped to some farms: the server refuses an
   *  every-farm notice from them, so the choice is shown but closed. */
  canPickEveryFarm: boolean;
}) {
  const id = useId();

  const roleItems: PickItem[] = useMemo(
    () =>
      (options?.roles ?? []).map((r) => ({
        value: r.code,
        label: r.nameEn,
        detail: r.code,
        keywords: r.nameUr ?? undefined,
      })),
    [options],
  );
  const farmItems: PickItem[] = useMemo(
    () => (options?.farms ?? []).map((f) => ({ value: f.id, label: `${f.code} — ${f.nameEn}` })),
    [options],
  );

  return (
    <div className="audience">
      <fieldset className="audience__group">
        <legend className="field__label">People</legend>
        <div className="audience__choices">
          <label className="settings__toggle">
            <input
              type="radio"
              name={`${id}-people`}
              checked={value.allRoles}
              onChange={() => onChange({ ...value, allRoles: true })}
            />
            Everyone
          </label>
          <label className="settings__toggle">
            <input
              type="radio"
              name={`${id}-people`}
              checked={!value.allRoles}
              onChange={() => onChange({ ...value, allRoles: false })}
            />
            Only some roles
          </label>
        </div>

        {!value.allRoles && (
          <div className="audience__detail">
            <PickList
              label="Roles"
              items={roleItems}
              value={value.roleCodes}
              onChange={(roleCodes) => onChange({ ...value, roleCodes })}
              noun={{ one: 'role', many: 'roles' }}
              searchPlaceholder={`Find a role among ${roleItems.length}`}
              max={MAX_ROLES}
              tooMany={{ label: 'Choose Everyone instead', onClick: () => onChange({ ...value, allRoles: true }) }}
              unknownLabel="A role no longer in use"
            />
          </div>
        )}
      </fieldset>

      <fieldset className="audience__group">
        <legend className="field__label">Farms</legend>
        <div className="audience__choices">
          <label className={`settings__toggle${canPickEveryFarm ? '' : ' audience__choice--off'}`}>
            <input
              type="radio"
              name={`${id}-farms`}
              checked={value.allFarms}
              disabled={!canPickEveryFarm}
              onChange={() => onChange({ ...value, allFarms: true })}
            />
            Every farm
          </label>
          <label className="settings__toggle">
            <input
              type="radio"
              name={`${id}-farms`}
              checked={!value.allFarms}
              onChange={() => onChange({ ...value, allFarms: false })}
            />
            Only some farms
          </label>
        </div>
        {!canPickEveryFarm && (
          <p className="settings__hint">You can only address the farms you look after.</p>
        )}

        {!value.allFarms && (
          <div className="audience__detail">
            <PickList
              label="Farms"
              items={farmItems}
              value={value.farmIds}
              onChange={(farmIds) => onChange({ ...value, farmIds })}
              noun={{ one: 'farm', many: 'farms' }}
              searchPlaceholder={`Search ${farmItems.length} farms by code or name`}
              max={MAX_FARMS}
              tooMany={
                canPickEveryFarm
                  ? { label: 'Choose Every farm instead', onClick: () => onChange({ ...value, allFarms: true }) }
                  : undefined
              }
              unknownLabel="A farm you cannot see"
              wide
            />
          </div>
        )}
      </fieldset>
    </div>
  );
}
